import { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { PageHeader } from "../../../components/ui";
import dayjs from "dayjs";
import { ClioService, LloFunnelReportItem, LloFunnelReportRequest, OpenAPI } from "../../../api";
import {
  fetchClioCustomFieldOptionNames,
  fetchClioUserNames,
} from "../../../components/shared/ClioSelects";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { LloFunnelFilters } from "./LloFunnel/LloFunnelFilters";
import { LloFunnelTable } from "./LloFunnel/LloFunnelTable";
import {
  mergeUniqueOptions,
  resetFilters,
  uniqueFilterOptions,
} from "./LloFunnel/filtering";
import { computeCaseGroupSpans, groupRowsByCase, sortReportRows } from "./LloFunnel/sorting";
import {
  DEFAULT_SORT_RULES,
  EMPTY_FILTERS,
  NEGOTIATION_FILTER_OPTIONS,
  OVERDUE_FILTER_OPTIONS,
  type FilterFieldKey,
  type LloFunnelFiltersState,
  type SortRule,
} from "./LloFunnel/types";

const MySwal = withReactContent(Swal);

const REPORT_DERIVED_FIELDS: FilterFieldKey[] = ["raa", "ap", "taskName", "taskAssignee"];

type PreloadedFilterOptions = Partial<Record<FilterFieldKey, string[]>>;

function formatDateParam(value: LloFunnelFiltersState["dueDateFrom"]): string | null {
  return value ? dayjs(value).format("YYYY-MM-DD") : null;
}

function toReportRequest(filters: LloFunnelFiltersState): LloFunnelReportRequest {
  return {
    caseName: filters.caseName || null,
    raa: filters.raa || null,
    ap: filters.ap || null,
    negotiation: filters.negotiation || null,
    funnel: filters.funnel || null,
    taskName: filters.taskName || null,
    taskAssignee: filters.taskAssignee || null,
    overdueOnly:
      filters.overdue === "Yes" ? true : filters.overdue === "No" ? false : null,
    startDate: formatDateParam(filters.dueDateFrom),
    endDate: formatDateParam(filters.dueDateTo),
    lloExpirationStartDate: formatDateParam(filters.lloExpirationFrom),
    lloExpirationEndDate: formatDateParam(filters.lloExpirationTo),
  };
}

function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.style.display = "none";
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
}

export default function LloFunnelReport() {
  const [reportData, setReportData] = useState<LloFunnelReportItem[]>([]);
  const [preloadedOptions, setPreloadedOptions] = useState<PreloadedFilterOptions>({});
  const [filters, setFilters] = useState<LloFunnelFiltersState>(EMPTY_FILTERS);
  const [sortRules, setSortRules] = useState<SortRule[]>([...DEFAULT_SORT_RULES]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let cancelled = false;

    const loadFilterOptions = async () => {
      const [mattersResult, raa, ap, taskAssignee] = await Promise.all([
        ClioService.getApiClioLloMatters().catch(() => []),
        fetchClioCustomFieldOptionNames("raa"),
        fetchClioCustomFieldOptionNames("ap"),
        fetchClioUserNames(true),
      ]);

      if (cancelled) return;

      const caseName = [
        ...new Set(
          mattersResult
            .map((m) => (m.description ?? m.display_number ?? "").trim())
            .filter(Boolean)
        ),
      ].sort((a, b) => a.localeCompare(b));

      setPreloadedOptions({ caseName, raa, ap, taskAssignee });
    };

    void loadFilterOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchReport = useCallback((nextFilters: LloFunnelFiltersState) => {
    setLoading(true);
    ClioService.postApiClioLloFunnelReport(toReportRequest(nextFilters))
      .then((data) => {
        setReportData(data);
        setHasLoaded(true);
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading LLO funnel report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, []);

  const loadReport = useCallback(() => {
    fetchReport(filters);
  }, [fetchReport, filters]);

  const applyFilters = useCallback(
    (nextFilters: LloFunnelFiltersState, nextSortRules: SortRule[]) => {
      setSortRules(nextSortRules.length > 0 ? nextSortRules : [...DEFAULT_SORT_RULES]);
      setPage(0);

      const filtersChanged =
        JSON.stringify(toReportRequest(filters)) !== JSON.stringify(toReportRequest(nextFilters));
      setFilters(nextFilters);

      if (filtersChanged) {
        fetchReport(nextFilters);
      }
    },
    [fetchReport, filters]
  );

  const clearFilters = useCallback(() => {
    const empty = resetFilters();
    setFilters(empty);
    setPage(0);
    if (hasLoaded) {
      fetchReport(empty);
    }
  }, [fetchReport, hasLoaded]);

  const filterOptions = useMemo(() => {
    const fromReport = {} as Record<FilterFieldKey, string[]>;
    REPORT_DERIVED_FIELDS.forEach((field) => {
      fromReport[field] = uniqueFilterOptions(reportData, field);
    });

    return {
      caseName: preloadedOptions.caseName ?? [],
      raa: mergeUniqueOptions(preloadedOptions.raa, fromReport.raa),
      ap: mergeUniqueOptions(preloadedOptions.ap, fromReport.ap),
      negotiation: [...NEGOTIATION_FILTER_OPTIONS],
      funnel: [],
      taskName: fromReport.taskName ?? [],
      taskAssignee: mergeUniqueOptions(preloadedOptions.taskAssignee, fromReport.taskAssignee),
      overdue: [...OVERDUE_FILTER_OPTIONS],
    } as Record<FilterFieldKey, string[]>;
  }, [reportData, preloadedOptions]);

  const sortedData = useMemo(
    () => sortReportRows(reportData, sortRules),
    [reportData, sortRules]
  );

  const caseGroups = useMemo(() => groupRowsByCase(sortedData), [sortedData]);

  const paginatedRows = useMemo(() => {
    const pageGroups = caseGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return computeCaseGroupSpans(pageGroups.flat());
  }, [caseGroups, page, rowsPerPage]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/lloFunnelReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(toReportRequest(filters)),
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      triggerBlobDownload(await response.blob(), `LloFunnelReport-${dayjs().format("YYYY-MM-DD")}.xlsx`);
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error downloading LLO funnel report.</p>,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "var(--app-content-height)",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        overflow: "hidden",
      }}
    >
      <PageHeader
        title="LLO funnel"
        subtitle="Limited litigation (LLO) funnel tasks with descriptions modified from the standard template."
      />

      <LloFunnelFilters
        filters={filters}
        filterOptions={filterOptions}
        sortRules={sortRules}
        loading={loading}
        downloading={downloading}
        canDownload={hasLoaded && reportData.length > 0}
        onLoad={loadReport}
        onApply={applyFilters}
        onDownload={handleDownload}
        onClear={clearFilters}
      />

      <LloFunnelTable
        loading={loading}
        hasLoadedData={hasLoaded}
        uniqueCaseCount={caseGroups.length}
        taskCount={reportData.filter((r) => (r.taskId ?? 0) > 0).length}
        rows={paginatedRows}
        caseGroupCount={caseGroups.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(0);
        }}
      />
    </Box>
  );
}
